import { useCallback, useEffect, useState } from "react";
import { authDisabled, getWorkflowAccessToken, isAuthConfigured, loginRequest, msal, reviewerPreviewSandboxEnabled, workflowEnabled } from "./auth";
import AdminSettingsPanel from "./AdminSettingsPanel";
import AdminRoleAssignments from './AdminRoleAssignments';
import AdminSecurityStatus from './AdminSecurityStatus';
import CentralCcdApproversPanel from './CentralCcdApproversPanel';
import { prepareAdminSettings } from './admin-compatibility';
import { adminSections, adminSessionSettings } from './admin-navigation';
import { getAdminSettingsPreview, getWorkflowIdentity } from "./workflow-api";

function message(error) {
  return error instanceof Error ? error.message : String(error);
}

function AdminAuthScreen({ title, description, action, onAction, error }) {
  return <main className="auth-shell">
    <section className="auth-card">
      <img className="auth-logo" src="/rawabi-commercial-fullcolor.png" alt="Rawabi" />
      <p className="eyebrow">Profile Picture Studio</p>
      <h1>{title}</h1>
      {description && <p className="auth-description">{description}</p>}
      {action && <button className="auth-button" onClick={onAction}>{action}</button>}
      {error && <p className="auth-error">{error}</p>}
      <a href="/">Back to Profile Picture Studio</a>
    </section>
  </main>;
}

export default function AdminApp() {
  const [status, setStatus] = useState("loading");
  const [account, setAccount] = useState(null);
  const [identity, setIdentity] = useState(null);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState("");
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [settingsDirty, setSettingsDirty] = useState(false);
  const [ccdDirty, setCcdDirty] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [ccdSaving, setCcdSaving] = useState(false);
  const [rolesDirty, setRolesDirty] = useState(false);
  const [rolesSaving, setRolesSaving] = useState(false);
  const [superAdminEditMode, setSuperAdminEditMode] = useState(false);
  const [activeSection, setActiveSection] = useState('approvals');
  const [refreshVersion, setRefreshVersion] = useState(0);
  const dirty = settingsDirty || ccdDirty || rolesDirty;
  const saving = settingsSaving || ccdSaving || rolesSaving;
  const discardEdits = () => !dirty || window.confirm('Discard unsaved Admin changes?');

  useEffect(() => {
    if (authDisabled) {
      setStatus("configuration");
      setError("Admin workspace requires Microsoft Entra authentication.");
      return;
    }
    if (!workflowEnabled || !isAuthConfigured || !msal) {
      setStatus("configuration");
      setError("Workflow Entra/API configuration is required for the Admin workspace.");
      return;
    }
    let active = true;
    (async () => {
      try {
        await msal.initialize();
        const redirect = await msal.handleRedirectPromise();
        const signedIn = redirect?.account ?? msal.getAllAccounts()[0] ?? null;
        if (signedIn) msal.setActiveAccount(signedIn);
        if (!active) return;
        setAccount(signedIn);
        setStatus(signedIn ? "authenticated" : "anonymous");
      } catch (authError) {
        if (!active) return;
        setError(message(authError));
        setStatus("anonymous");
      }
    })();
    return () => { active = false; };
  }, []);

  const getAccessToken = useCallback(() => getWorkflowAccessToken(account), [account]);

  async function loadSettings() {
    if (!account) return;
    if (saving || !discardEdits()) return;
    setSuperAdminEditMode(false);
    setLoadingSettings(true);
    setError("");
    try {
      const me = await getWorkflowIdentity(getAccessToken);
      setIdentity(me.identity);
      if (!me.identity?.capabilities?.adminSettings) {
        setSettings(null);
        setStatus("forbidden");
        return;
      }
      const result = await getAdminSettingsPreview(getAccessToken);
      setSettings(prepareAdminSettings(result, me.identity));
      setRefreshVersion(value => value + 1);
      setSettingsDirty(false);
      setCcdDirty(false);
      setRolesDirty(false);
    } catch (loadError) {
      setError(message(loadError));
    } finally {
      setLoadingSettings(false);
    }
  }

  useEffect(() => {
    if (status === "authenticated" && account) loadSettings();
  }, [status, account?.homeAccountId]);

  async function signIn() {
    setError("");
    try { await msal.loginRedirect(loginRequest); }
    catch (authError) { setError(message(authError)); }
  }

  async function signOut() {
    if (saving || !discardEdits()) return;
    try { await msal.logoutRedirect({ account, postLogoutRedirectUri: window.location.origin }); }
    catch (authError) { setError(message(authError)); }
  }

  if (status === "loading") return <AdminAuthScreen title="Opening Admin workspace…" />;
  if (status === "configuration") return <AdminAuthScreen title="Admin workspace unavailable" description="The secure workflow configuration is incomplete." error={error} />;
  if (status === "anonymous") return <AdminAuthScreen title="Admin sign in" description="Use a Rawabi Microsoft account authorized to manage this application." action="Sign in with Microsoft" onAction={signIn} error={error} />;
  if (status === "forbidden") return <AdminAuthScreen title="Admin access required" description="This account does not have access to application management." action="Switch account" onAction={signIn} error={error} />;

  const isSuperAdmin = settings?.permissions?.viewAccess === true;
  const writerReady = settings?.writeStatus === 'ready' && !settings.compatibilityNotice;
  const panelSettings = adminSessionSettings(settings, superAdminEditMode);
  const accessSecurity = settings?.security;
  const sections = adminSections(settings?.permissions);
  const selectedSection = sections.some(section => section.id === activeSection) ? activeSection : sections[0]?.id;

  return <main className="admin-app-shell">
    <header className="admin-app-header">
      <div><p className="eyebrow">Profile Picture Studio</p><h1>Admin workspace</h1><small>{identity?.name || identity?.email}</small>
        {settings && <p role="status">{settings.compatibilityNotice ? 'Access: Admin confirmed; updated API required to verify SuperAdmin access.' : isSuperAdmin ? 'Access: SuperAdmin — confirmed by the API' : 'Access: Admin'}</p>}
      </div>
      <div className="admin-app-actions">
        {isSuperAdmin && <button type="button" aria-pressed={superAdminEditMode} disabled={saving || loadingSettings || !writerReady} onClick={() => setSuperAdminEditMode((current) => !current)} title={writerReady ? "SuperAdmin edit mode resets automatically on refresh." : "Editing is not available from the API. Refresh after deploying the updated API."}>{superAdminEditMode ? "Lock editing" : "Enable editing"}</button>}
        <a href="/" onClick={event => { if (saving || !discardEdits()) event.preventDefault(); }}>Studio</a>
        {(identity?.capabilities?.reviewer || (reviewerPreviewSandboxEnabled && isSuperAdmin)) && <a href="/reviewer/" onClick={event => { if (saving || !discardEdits()) event.preventDefault(); }}>{reviewerPreviewSandboxEnabled && isSuperAdmin ? "Preview Reviewer Portal" : "Reviewer Portal"}</a>}
        {identity?.capabilities?.directUpdater && <a href="/direct-update/" onClick={event => { if (saving || !discardEdits()) event.preventDefault(); }}>Direct Updater</a>}
        <button onClick={loadSettings} disabled={loadingSettings || saving}>{loadingSettings ? "Refreshing…" : "Refresh"}</button>
        <button onClick={signOut} disabled={saving}>Sign out</button>
      </div>
    </header>
    {reviewerPreviewSandboxEnabled && <div className="workflow-notice" role="status"><strong>Preview environment</strong> — Reviewer validation uses real Production read-only data. Preview write actions remain disabled; Entra roles, workflow records, Admin settings and Microsoft 365 are not modified.</div>}
    {isSuperAdmin && writerReady && <p className="admin-session-edit-note" role="status">{superAdminEditMode ? "Editing enabled. Each saved change is recorded with your reason." : "Viewing settings. Enable editing to make changes. Unsaved drafts are kept when you lock editing."}</p>}
    {error && <div className="workflow-error" role="alert">{error}</div>}
    {panelSettings ? <div className="admin-workspace-layout" key={refreshVersion}>
      <nav className="admin-workspace-nav" aria-label="Admin sections">
        {sections.map(section => <button key={section.id} type="button" aria-current={selectedSection === section.id ? 'page' : undefined} onClick={() => setActiveSection(section.id)}>{section.label}</button>)}
        {dirty && <small role="status">Unsaved changes</small>}
      </nav>
      <div className="admin-workspace-content" aria-busy={saving || loadingSettings}>
        <div hidden={selectedSection === 'security'}>
          <AdminSettingsPanel
            settings={panelSettings}
            activeSection={selectedSection}
            getAccessToken={getAccessToken}
            onDirtyChange={setSettingsDirty}
            onBusyChange={setSettingsSaving}
            externallyBusy={ccdSaving || rolesSaving || loadingSettings}
            ccdPanel={<CentralCcdApproversPanel settings={panelSettings} getAccessToken={getAccessToken} onDirtyChange={setCcdDirty} onBusyChange={setCcdSaving} externallyBusy={settingsSaving || rolesSaving || loadingSettings} />}
            accessPanel={settings.permissions?.viewAccess === true ? <AdminRoleAssignments getAccessToken={getAccessToken} security={accessSecurity} companies={settings.editableCompanyRoutes || settings.companyRoutes || []} editing={superAdminEditMode} onDirtyChange={setRolesDirty} onBusyChange={setRolesSaving} externallyBusy={settingsSaving || ccdSaving || loadingSettings} /> : null}
          />
        </div>
        {selectedSection === 'security' && settings.permissions?.viewSecurity === true && <AdminSecurityStatus security={settings.security} editing={superAdminEditMode} />}
      </div>
    </div> : !loadingSettings && <p className="workflow-empty">Admin settings are not available.</p>}
  </main>;
}
